import React, { useMemo } from "react";
import {
  EuiIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiAccordion,
  EuiNotificationBadge,
} from "@elastic/eui";
import { CustomCheckbox } from "components/CustomInputs/CustomCheckbox";
import { List, RowComponentProps } from "react-window";
import "styles/components/filterDropdown/fd_filter_dropdown.css";

interface Option {
  label: string;
  value: string;
  display?: string;
  name?: string;
}

interface RowExtraProps {
  options: Option[];
  activeFilters: Record<string, "on" | "off" | undefined>;
  handleOptionClick: (option: Option) => void;
  isSingleSelection?: boolean;
  classes: Record<string, string>;
}

interface SideAccordionItemProps {
  option: { values: Option[]; name?: string; display?: string };
  activeFilters: Record<string, "on" | "off" | undefined>;
  setActiveFilters: (filters: Record<string, "on" | "off" | undefined>) => void;
  filterItemClick: (option: Option) => void;
  classes: Record<string, string>;
  onToggle?: (name: string, isOpen: boolean) => void;
  isOpen?: boolean;
  filterText?: string;
  isSingleSelection?: boolean;
  filtersCount?: number;
  loading?: boolean;
  allowExclude?: boolean;
}

export const SideAccordionItem = ({
  option,
  activeFilters,
  filterItemClick,
  classes,
  setActiveFilters,
  onToggle,
  isOpen,
  filterText,
  isSingleSelection = false,
  filtersCount = 0,
  loading,
  allowExclude,
}: SideAccordionItemProps) => {
  const bottomPadding = filtersCount * 38 + 52;
  const topPadding = 88;
  const count = option?.values?.length ?? 0;
  const vh75 = window.innerHeight * 0.8;
  const height = vh75 - (topPadding + bottomPadding);

  const processedOptions = useMemo(
    () =>
      option.values?.map((i) => {
        const val = `${i.value}`;
        return {
          ...i,
          value: i.value,
          checked:
            activeFilters[val] === "on" ? "on" : activeFilters[val] === "off" ? "off" : undefined,
        };
      }) ?? [],
    [option.values, activeFilters]
  );

  const filteredOptions = useMemo(() => {
    if (!filterText) return processedOptions;
    return processedOptions.filter((opt) => {
      const value = opt.name ?? "";
      const display = opt.display ?? value;
      return (
        value.toLowerCase().includes(filterText.toLowerCase()) ||
        display.toLowerCase().includes(filterText.toLowerCase())
      );
    });
  }, [processedOptions, filterText]);

  const checkedCount = useMemo(
    () => processedOptions.filter((o) => activeFilters[o.value] === "on").length,
    [processedOptions, activeFilters]
  );
  const negativeCheckedCount = useMemo(
    () => processedOptions.filter((o) => activeFilters[o.value] === "off").length,
    [processedOptions, activeFilters]
  );
  const totalCheckedCount = checkedCount + negativeCheckedCount;
  const areAllSelected = processedOptions.length > 0 && checkedCount === processedOptions.length;
  const areAllNegativeSelected =
    processedOptions.length > 0 && negativeCheckedCount === processedOptions.length;

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newFilters = { ...activeFilters };
    if (areAllSelected) {
      if (allowExclude) processedOptions.forEach((o) => (newFilters[o.value] = "off"));
      if (!allowExclude) processedOptions.forEach((o) => delete newFilters[o.value]);
    } else if (areAllNegativeSelected) {
      processedOptions.forEach((o) => delete newFilters[o.value]);
    } else {
      processedOptions.forEach((o) => (newFilters[o.value] = "on"));
    }
    setActiveFilters(newFilters);
  };

  const handleOptionClick = (opt: Option) => {
    filterItemClick({
      name: opt.value,
      value: opt.value,
      label: opt.value,
      display: opt.value,
    });
  };

  // Row renderer for react-window
  const Row = ({
    index,
    style,
    ariaAttributes,
    options,
    activeFilters,
    handleOptionClick,
    isSingleSelection,
    classes,
  }: RowComponentProps<RowExtraProps>) => {
    const opt: any = options[index];
    return (
      <div
        {...ariaAttributes}
        style={style}
        className={classes.tagOptionRow}
        onClick={() => handleOptionClick(opt)}>
        <EuiFlexGroup style={{ padding: "0 4px" }} alignItems="center" gutterSize="s">
          {isSingleSelection && (
            <EuiFlexItem grow={false}>
              <EuiIcon type="filter" style={{ marginRight: "5px" }} size="s" color="#9170BB" />
            </EuiFlexItem>
          )}
          {!isSingleSelection && (
            <CustomCheckbox
              onClick={() => {}}
              isChecked={activeFilters[opt?.value] === "on"}
              isExcluded={activeFilters[opt?.value] === "off"}
            />
          )}
          <EuiFlexItem>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: opt?.name?.length > 50 ? "11px" : "14px",
              }}>
              {opt?.display ?? opt?.name}
            </span>
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>
    );
  };

  const messaage = option?.name === "Table" && count === 0 ? "Select a database first" : count;

  const accordionButton = (
    <EuiFlexGroup style={{ height: "38px" }} alignItems="center" gutterSize="s">
      {isSingleSelection && <EuiFlexItem grow={false}></EuiFlexItem>}
      {!isSingleSelection && (
        <CustomCheckbox
          onClick={handleCheckboxClick}
          isChecked={areAllSelected}
          isExcluded={areAllNegativeSelected}
          isPartial={totalCheckedCount > 0}
        />
      )}
      <EuiFlexItem>
        <span style={{ display: "inline-flex", alignItems: "center", fontWeight: 500 }}>
          {option.display ?? option.name}
          <span style={{ fontWeight: 400, paddingLeft: "6px", fontSize: "var(--fs12)" }}>
            ({messaage})
          </span>
        </span>
      </EuiFlexItem>
      {totalCheckedCount > 0 && (
        <EuiFlexItem grow={false}>
          <EuiNotificationBadge>{totalCheckedCount}</EuiNotificationBadge>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  );

  if (filterText && filteredOptions.length === 0) return null;

  return (
    <div className={classes.tagAccordionButton}>
      <EuiAccordion
        isLoading={loading}
        id={`filter-accordion-${option.name}`}
        buttonContent={accordionButton}
        paddingSize="s"
        buttonProps={{ style: { width: "100%", display: "block" } }}
        forceState={isOpen ? "open" : "closed"}
        onToggle={(open) => onToggle?.(option?.name || "", open)}
        extraAction={null}
        arrowDisplay={filteredOptions.length > 0 ? "right" : "none"}>
        <List<RowExtraProps>
          rowCount={filteredOptions.length}
          rowHeight={38}
          rowComponent={Row}
          rowProps={{
            options: filteredOptions,
            activeFilters,
            handleOptionClick,
            isSingleSelection,
            classes,
          }}
          overscanCount={5}
          style={{
            height:
              filterText || filteredOptions.length < 10 ? filteredOptions.length * 38 : height,
          }}
        />
      </EuiAccordion>
    </div>
  );
};
